import React from 'react';
import styled from 'styled-components';
import { CheckCircle2 } from 'lucide-react';
import { FONT_SANS, ROADMAP_LAYOUT } from '../constants';
import { colors } from '../theme/colors';
import { useLanguage } from '../context/LanguageContext';

const Section = styled.section`
  padding: clamp(72px, 10vh, 112px) clamp(20px, 5vw, 48px) 96px;
  background: ${colors.ink};
  color: ${colors.primaryText};
`;

const Inner = styled.div`
  width: min(900px, 100%);
  margin: 0 auto;
`;

const Title = styled.h2`
  font-family: ${FONT_SANS};
  font-size: clamp(1.75rem, 4vw, 2.25rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  text-align: center;
  margin: 0 0 12px;
`;

const Lead = styled.p`
  font-family: ${FONT_SANS};
  font-size: 1.05rem;
  line-height: 1.6;
  color: #94a3b8;
  text-align: center;
  margin: 0 auto 48px;
  max-width: 520px;
`;

const Timeline = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const Item = styled.li`
  display: grid;
  grid-template-columns: 28px 1fr;
  gap: 20px;
  position: relative;
  padding-bottom: 36px;

  &:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 13px;
    top: 28px;
    bottom: 0;
    width: 2px;
    background: rgba(148, 163, 184, 0.25);
  }
`;

const Dot = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
  box-shadow: 0 0 0 4px rgba(44, 42, 41, 0.9);
`;

const Card = styled.article`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 16px;
  padding: 20px 22px;
`;

const Period = styled.p`
  margin: 0 0 4px;
  font-family: ${FONT_SANS};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
`;

const Phase = styled.h3`
  margin: 0 0 8px;
  font-family: ${FONT_SANS};
  font-size: 1.15rem;
  font-weight: 800;
  color: #f8fafc;
`;

const Status = styled.span`
  display: inline-block;
  margin-bottom: 14px;
  padding: 4px 10px;
  border-radius: 999px;
  font-family: ${FONT_SANS};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ $color }) => $color};
  background: ${({ $color }) => `${$color}22`};
  border: 1px solid ${({ $color }) => `${$color}44`};
`;

const TaskList = styled.ul`
  margin: 0 0 12px;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Task = styled.li`
  font-family: ${FONT_SANS};
  font-size: 14px;
  line-height: 1.5;
  color: #cbd5e1;
  display: flex;
  gap: 8px;
  align-items: flex-start;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
    color: #64748b;
  }
`;

const Detail = styled.p`
  margin: 0;
  font-family: ${FONT_SANS};
  font-size: 13px;
  line-height: 1.55;
  color: #94a3b8;
  padding-top: 12px;
  border-top: 1px solid rgba(148, 163, 184, 0.15);
`;

const PHASE_COLORS = {
  accent: colors.accent,
  couponMedium: colors.couponMedium,
  couponEpic: colors.couponEpic,
  primary: colors.primaryText,
};

function Roadmap() {
  const { t } = useLanguage();

  return (
    <Section id="roadmap" aria-labelledby="roadmap-title">
      <Inner>
        <Title id="roadmap-title">{t.roadmap.title}</Title>
        <Lead>{t.roadmap.lead}</Lead>
        <Timeline>
          {ROADMAP_LAYOUT.map((layout) => {
            const phase = t.roadmap.phases[layout.id];
            const dotColor = PHASE_COLORS[layout.colorKey] || colors.accent;
            return (
              <Item key={layout.id}>
                <Dot $color={dotColor} aria-hidden="true" />
                <Card>
                  <Period>{phase.period}</Period>
                  <Phase>{phase.phase}</Phase>
                  <Status $color={dotColor}>{phase.status}</Status>
                  <TaskList>
                    {phase.tasks.map((task) => (
                      <Task key={task}>
                        <CheckCircle2 size={14} aria-hidden="true" />
                        {task}
                      </Task>
                    ))}
                  </TaskList>
                  {phase.details.map((line) => (
                    <Detail key={line}>{line}</Detail>
                  ))}
                </Card>
              </Item>
            );
          })}
        </Timeline>
      </Inner>
    </Section>
  );
}

export default Roadmap;
